import {
  FieldList,
  IFormGroupRules,
  IFormRules,
} from '@components/form/services';
import {
  CELL_FILTER,
  CELL_RENDERER,
  COLUMN_SELECTOR_ID,
  DROPDOWN_MENU_POSITION,
  ENTITY_TYPES,
  ISite,
  TOOLBAR_BUTTON_POSITION,
  TRANSFER_TYPE,
  TRANSFER_TYPE_ENUM_OPTIONS,
  WIDGET_COMPONENT,
  XUX_GRID_ACTIONS,
} from '@shared/types';
import { filter, get, head, includes, isEmpty, isEqual } from 'lodash';
import {
  DISPLAY_EVENT_TYPES,
  TRANSFER_UNSAVED_STATUS,
} from '@shared/services/transfer/transfer.definition';
import {
  TRANSFER_STATUS_OPTIONS,
  TRANSFER_STATUS_VALUE,
} from '@app/shared/types/enums/transfer-status.enum';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import {
  ACTIONS_PANEL_BUTTON,
  BTN_ADD_SITES,
  BTN_REMOVE,
  GRID_EDIT_BTN,
} from '@text-content/button-templates';
import { BUTTON_FLEX_ORDER } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/constants';
import { TransferAutoRestockFormService } from './transfer-auto-restock.form.service';
import { ValueGetterParams } from 'ag-grid-community';
import { stringCaseInsensitiveComparator } from '@app/shared/common/string-case-insensitive.comparator';
import { alphanumericComparator } from '@shared/common';
import { COLUMN_ALIGN_RIGHT } from '@shared/constants/column-align-definition';
import { WIDGET_FAMILY } from '@config/index';
import { COMMON_GENERAL_MESSAGE_CLASS } from '@app/shared/constants/common-warn-grid-setting';
import {
  BTN_BULK_REPLENISH_ENDING_COUNT_OR_EXPECTED_QTY,
  BTN_BULK_REPLENISH_QOH,
  BTN_BULK_REPLENISH_ZERO_QOH,
  GROUP_BTN_BULK_REPLENISH_BASE,
} from '../transfer-replenish/transfer-replenish.constants';
import { transferGeneralFields } from '@shared/services/transfer/transfer-column.definition';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { getObservableValue } from '@app/shared/utils';
import { I18n } from '@app/i18n/strings-keys';
import { TransferEditContainerComponent } from '@app/test/shared/editors-containers';

export const createFormRules = (
  formService: TransferAutoRestockFormService,
  editor: TransferEditContainerComponent,
): IFormRules => {
  const sections: FieldList = {
    general_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: I18n.Common.General,
    },
    grand_totals_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: '',
    },
    transfer_destinations_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: '',
    },
  };

  const panelGroups: IFormGroupRules[] = [
    {
      name: I18n.Common.General,
      fields: [
        'general_section',
        'grand_totals_section',
        'transfer_destinations_section',
      ],
    },
    {
      id: 'general_section',
      fields: [
        'type',
        'open_draft_message',
        'transfer_type',
        'source',
        'event_id',
        'transaction_date',
        'virtual.transaction_date',
        'status',
      ],
    },
    {
      id: 'grand_totals_section',
      fields: ['virtual.grand_totals'],
    },
    {
      id: 'transfer_destinations_section',
      fields: [
        'virtual.transfer_destinations_info_msg',
        'virtual.transfer_destinations_no_sites_info_msg',
        'virtual.transfer_destinations',
        'virtual.selected_event',
      ],
    },
  ];

  return {
    defaultGroup: false,
    virtual: {
      type: 'object',
      properties: {
        totals: {
          type: 'array',
        },
        transaction_date: {
          type: ['string', 'object'],
        },
        grand_totals: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              department_code: { type: 'string' },
              request_count: { type: 'number' },
            },
          },
        },
        transfer_destinations: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string' },
              item_count: { type: 'number' },
              total_cost: { type: 'number' },
            },
          },
        },
        selected_event: {
          type: 'object',
        },
      },
    },
    fields: {
      ...sections,
      ...transferGeneralFields({ i18nService: editor.i18nService }),
      type: {
        show: false,
        defaultValue: ENTITY_TYPES.TRANSFER,
      },
      transfer_type: {
        title: I18n.Common.Type,
        widget: WIDGET_COMPONENT.select,
        optionText: TRANSFER_TYPE_ENUM_OPTIONS,
        defaultValue: TRANSFER_TYPE.PRE_EVENT_TRANSFER,
        disabled: true,
      },
      site_id: {
        show: false,
        required: false,
      },
      site_id_to: {
        show: false,
        required: false,
      },
      event_id: {
        title: I18n.EntityNames.Event,
        widget: WIDGET_COMPONENT.entity_select,
        required: true,
        widgetData: {
          entity: ENTITY_TYPES.EVENTS,
          dataSource: formService.eventIdDataSource,
          lazy: true,
          hideEmptyOption: true,
          enableClientFiltering: true,
        },
        show: ({ transfer_type }): boolean =>
          includes(DISPLAY_EVENT_TYPES, transfer_type),
        omit: ({ transfer_type }): boolean =>
          !includes(DISPLAY_EVENT_TYPES, transfer_type),
        validators: [formService.validationService.requiredValidator],
      },
      transaction_date: {
        title: I18n.Common.Date,
        widget: WIDGET_COMPONENT.xux_datepicker,
        required: true,
        widgetData: {
          minimumObs: formService.transactionDateLimit.minimumObs,
          maximumObs: formService.transactionDateLimit.maximumObs,
        },
        validators: [
          (control: AbstractControl): ValidationErrors | null =>
            formService.getTransactionDateRangeValidator(
              getObservableValue(formService.transactionDateLimit.maximumObs),
              getObservableValue(formService.transactionDateLimit.minimumObs),
              true,
            )(control),
        ],
        disabled: (): boolean => formService.getTransferDateDisable(),
      },
      'virtual.transaction_date': {
        show: false,
      },
      status: {
        title: I18n.Common.Status,
        disabled: true,
        widget: WIDGET_COMPONENT.select,
        required: false,
        selectOptions: TRANSFER_STATUS_OPTIONS,
        defaultValue: TRANSFER_STATUS_VALUE.New,
        show: false,
      },
      'virtual.grand_totals': {
        widget: WIDGET_COMPONENT.edit_grid,
        title: I18n.Common.GrandTotals,
        class: 'navigate-grid',
        widgetData: {
          filterable: false,
          showSearch: false,
          canAdd: false,
          canEdit: false,
          orderControls: false,
          canRemove: false,
          hideActionPanel: true,
          disableColumnSelector: true,
          multiselect: false,
          columns: [
            {
              path: 'department_code',
              title: I18n.EntityNames.DepartmentCode,
              sortable: true,
              defaultView: true,
              flex: 1,
              comparator: stringCaseInsensitiveComparator,
            },
            {
              path: 'request_cost',
              title: I18n.Forms.TransferForm.RequestCost,
              sortable: true,
              defaultView: true,
              flex: 1,
              valueFormatter:
                formService.numberFormatService.getNumberFormatter(true),
              comparator: (valueA, valueB): number =>
                alphanumericComparator(valueA, valueB),
              ...COLUMN_ALIGN_RIGHT,
            },
            {
              path: '',
              defaultView: true,
              flex: 1,
            },
          ],
          userGridOptions: {
            overlayNoRowsTemplate: formService.i18nService.instant(
              I18n.Common.NoRowsToShow,
            ),
          },
        },
      },
      'virtual.transfer_destinations_info_msg': {
        widget: WIDGET_COMPONENT.label,
        widgetFamily: WIDGET_FAMILY.LEGACY,
        title: I18n.InfoMessages.TransferDestinationsInfoMsg,
        show: true,
        hiddenObs: formService.hiddenDestinationInfo$,
        widgetData: {
          itemClass: COMMON_GENERAL_MESSAGE_CLASS,
        },
      },
      'virtual.transfer_destinations_no_sites_info_msg': {
        widget: WIDGET_COMPONENT.label,
        widgetFamily: WIDGET_FAMILY.LEGACY,
        title: I18n.InfoMessages.NoAvailableSites,
        show: true,
        hiddenObs: formService.hiddenDestinationNoSitesInfo$,
        widgetData: {
          itemClass: COMMON_GENERAL_MESSAGE_CLASS,
        },
      },
      'virtual.transfer_destinations': {
        widget: WIDGET_COMPONENT.edit_grid,
        title: I18n.EntityNames.TransferDestinations,
        class: 'no-padding',
        descriptionObs: formService.transferDestinationsDescription$,
        widgetData: {
          displayCountObs: formService.transferDestinationsDisplayCount$,
          selectedCountObs: formService.transferDestinationsSelectedCount$,
          useCustomButtonsOnFirstPosition: true,
          showSearch: true,
          multiselect: true,
          orderControls: false,
          disableColumnSelector: true,
          canRemove: true,
          showRowOptions: true,
          gridHeight: '600px',
          showLoader: formService.isGridBusy$,
          userGridOptions: {
            suppressNoRowsOverlay: true,
            isRowSelectable: formService.isRowSelectable.bind(formService),
            getRowClass({ data }): string {
              return formService.noPrimaryWarehouse(data)
                ? 'row-unselective'
                : '';
            },
            overlayNoRowsTemplate: formService.i18nService.instant(
              I18n.Common.NoRowsToShow,
            ),
          },
          buttons: [
            {
              ...GROUP_BTN_BULK_REPLENISH_BASE,
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              class: 'btn-primary np-non-effect button-right-padding-20',
              dropdownMenuPosition: DROPDOWN_MENU_POSITION.RIGHT,
              disabledObs: formService.disableBulkReplenishToPAR$,
              childItems: [
                {
                  ...BTN_BULK_REPLENISH_ENDING_COUNT_OR_EXPECTED_QTY,
                  click: formService
                    .getBulkReplenishClickHandler(
                      BTN_BULK_REPLENISH_ENDING_COUNT_OR_EXPECTED_QTY,
                    )
                    .bind(formService),
                  hiddenObs: formService.disableBulkReplenishToPAR$,
                },
                {
                  ...BTN_BULK_REPLENISH_QOH,
                  click: formService
                    .getBulkReplenishClickHandler(BTN_BULK_REPLENISH_QOH)
                    .bind(formService),
                },
                {
                  ...BTN_BULK_REPLENISH_ZERO_QOH,
                  click: formService
                    .getBulkReplenishClickHandler(BTN_BULK_REPLENISH_ZERO_QOH)
                    .bind(formService),
                },
              ],
            },
            {
              ...BTN_ADD_SITES,
              flexOrder: BUTTON_FLEX_ORDER.RIGHTMOST,
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              dropdownMenuPosition: DROPDOWN_MENU_POSITION.RIGHT,
              disabledObs: formService.disableAddSite$,
              click: formService.openSiteSelector.bind(formService),
            },
            {
              ...ACTIONS_PANEL_BUTTON,
              flexOrder: BUTTON_FLEX_ORDER.LEFTMOST,
              hidden: isEmpty,
              childItems: [
                {
                  ...GRID_EDIT_BTN,
                  value: null,
                  hidden: (selectedItems: ISite[]): boolean =>
                    get(selectedItems, 'length') > 1,
                  click: (data): Promise<void> =>
                    formService.editSiteInventoryItems(head(data)),
                },
                {
                  ...BTN_REMOVE,
                  class: 'xux-action-button btn full-width',
                },
              ],
            },
            {
              caption: I18n.Common.Edit,
              click: (value: string, gridItem: ISite): Promise<void> =>
                formService.editSiteInventoryItems(gridItem),
              position: TOOLBAR_BUTTON_POSITION.ROW,
            },
            {
              ...BTN_REMOVE,
              position: TOOLBAR_BUTTON_POSITION.ROW,
            },
          ],
          columns: [
            {
              path: 'store_number',
              title: I18n.Sites.SiteId,
              sortable: true,
              comparator: stringCaseInsensitiveComparator,
              defaultView: true,
              filterParams: {
                quickFilter: true,
              },
              cellRenderer: CELL_RENDERER.link_renderer,
              cellRendererParams: {
                onClick: (data): Promise<void> =>
                  formService.editSiteInventoryItems(data),
              },
            },
            {
              path: 'name',
              title: I18n.Sites.SiteName,
              sortable: true,
              defaultView: true,
              comparator: stringCaseInsensitiveComparator,
              filterParams: {
                quickFilter: true,
              },
              cellRenderer: CELL_RENDERER.link_renderer,
              cellRendererParams: {
                onClick: (data): Promise<void> =>
                  formService.editSiteInventoryItems(data),
              },
            },
            {
              path: 'site_name',
              title: I18n.Sites.FromPrimaryWarehouseBrackets,
              sortable: true,
              defaultView: true,
              comparator: stringCaseInsensitiveComparator,
              filterParams: {
                quickFilter: true,
              },
            },
            {
              path: 'status',
              title: I18n.Common.Status,
              sortable: true,
              defaultView: true,
              filterType: CELL_FILTER.enum,
              filterParams: {
                values: filter(TRANSFER_STATUS_OPTIONS, (item) =>
                  includes(TRANSFER_UNSAVED_STATUS, item.value),
                ),
              },
              valueGetter: (params: ValueGetterParams): string => {
                const statusValue = params.data?.status;

                return isEqual(statusValue, TRANSFER_STATUS_VALUE.Null)
                  ? ''
                  : formService.i18nService.instant(I18n.Status.New);
              },
            },
            {
              path: COLUMN_SELECTOR_ID,
              width: 50,
              maxWidth: 50,
              title: '',
              defaultView: true,
              cellRenderer: CELL_RENDERER.transfer_actions_renderer,
              pinned: detectMobile() ? false : 'right',
              lockPinned: true,
            },
          ],
          eventHandlers: {
            [XUX_GRID_ACTIONS.ROW_DOUBLE_CLICKED]:
              formService.handleDestinationGridAction.bind(formService),
            [XUX_GRID_ACTIONS.GRID_READY]:
              formService.handleDestinationGridAction.bind(formService),
          },
          composeConfirmationModalOptions:
            formService.composeConfirmationModalOptions.bind(formService),
        },
        validators: [formService.getTransferDestinationsValidator()],
      },
      [formService.formFieldPath.totalCost]: {
        show: false,
        required: false,
      },
      [formService.formFieldPath.virtualSelectedEvent]: {
        show: false,
        required: false,
        defaultValue: null,
      },
      [formService.formFieldPath.virtualTotals]: {
        show: false,
      },
    },
    groups: [...panelGroups],
  };
};
